import { useMemo, useState } from 'react';
import { useOutreachMailboxHealth } from '@discovr/features';
import type { OutreachMailboxHealthItem } from '@discovr/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { EnvelopeSimple } from '../../../design-system/icons';
import { cn } from '../../../lib/utils';
import { Donut } from '../insights/charts/Donut';
import { CrmCard, CrmCardError, CrmCardNote, CrmCardSkeleton } from './CrmCard';
import { formatRate } from './crmFormat';
import { crmLoadErrorCopy } from './crmLoadError';
import {
  POOL_NOTE,
  headroomSegments,
  mailboxName,
  mailboxLimitRows,
  mailboxStatusPill,
  tightestLimitText,
  type MailboxLimitRow,
} from './mailboxHealthFormat';
import { PILL_BASE } from './OutreachStatusPill';

const CARD_TITLE = 'Mailbox health';
const PICKER_LABEL = 'Mailbox';
/** What the card calls its content in an error line. */
export const MAILBOX_HEALTH_SUBJECT = 'mailbox health';
export const NO_MAILBOXES = 'No mailboxes are set up yet.';
const DONUT_SIZE = 140;
const DONUT_THICKNESS = 20;

/**
 * This campaign's sending mailboxes, least headroom first (Go's order), so
 * the default pick is the one closest to a limit. Before anything is sent Go
 * answers with the pool instead, and the card says so.
 */
export function CrmMailboxHealthCard({ campaignId }: { campaignId: string }) {
  return (
    <CrmCard title={CARD_TITLE}>
      <MailboxHealthBody campaignId={campaignId} />
    </CrmCard>
  );
}

function MailboxHealthBody({ campaignId }: { campaignId: string }) {
  const { data, isLoading, error } = useOutreachMailboxHealth(campaignId);
  const [pickedId, setPickedId] = useState<number | null>(null);
  const mailboxes = data?.mailboxes ?? [];
  const selected = mailboxes.find((mailbox) => mailbox.id === pickedId) ?? mailboxes[0];

  if (error) return <CrmCardError>{crmLoadErrorCopy(MAILBOX_HEALTH_SUBJECT, error)}</CrmCardError>;
  if (isLoading) return <CrmCardSkeleton />;
  if (!selected) return <CrmCardNote>{NO_MAILBOXES}</CrmCardNote>;

  return (
    <>
      <MailboxPicker mailboxes={mailboxes} selected={selected} onPick={setPickedId} />
      {data?.source === 'pool' && <p className="text-xs text-muted-foreground">{POOL_NOTE}</p>}
      <MailboxHealthDetail mailbox={selected} />
    </>
  );
}

interface MailboxPickerProps {
  mailboxes: readonly OutreachMailboxHealthItem[];
  selected: OutreachMailboxHealthItem;
  onPick: (id: number) => void;
}

/** One mailbox is named in plain text; several get Figma's dropdown with a check on the one shown. */
function MailboxPicker({ mailboxes, selected, onPick }: MailboxPickerProps) {
  if (mailboxes.length === 1) {
    return (
      <p className="truncate text-sm font-medium text-foreground" title={mailboxName(selected)}>
        {mailboxName(selected)}
      </p>
    );
  }
  return (
    <Select value={String(selected.id)} onValueChange={(value) => onPick(Number(value))}>
      <SelectTrigger aria-label={PICKER_LABEL} title={mailboxName(selected)} className="w-full max-w-full rounded-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent position="popper" align="start">
        {mailboxes.map((mailbox) => (
          <SelectItem key={mailbox.id} value={String(mailbox.id)} title={mailboxName(mailbox)}>
            <EnvelopeSimple aria-hidden />
            <span className="truncate">{mailboxName(mailbox)}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function MailboxHealthDetail({ mailbox }: { mailbox: OutreachMailboxHealthItem }) {
  const segments = useMemo(() => headroomSegments(mailbox.headroom), [mailbox.headroom]);
  const headroom = formatRate(mailbox.headroom);
  const tightest = tightestLimitText(mailbox.tightest);

  return (
    <>
      <MailboxStatus mailbox={mailbox} />
      <div className="flex justify-center py-1">
        <div role="img" aria-label={`${mailboxName(mailbox)}: ${headroom} headroom left.`} className="relative flex items-center justify-center">
          <Donut segments={segments} size={DONUT_SIZE} thickness={DONUT_THICKNESS} />
          <div aria-hidden className="absolute flex flex-col items-center">
            <span className="text-2xl font-semibold tabular-nums text-foreground">{headroom}</span>
            <span className="text-[11px] text-muted-foreground">headroom left</span>
          </div>
        </div>
      </div>
      {tightest && <p className="text-xs text-muted-foreground">{tightest}</p>}
      <dl className="space-y-1.5 text-sm">
        {mailboxLimitRows(mailbox).map((row) => (
          <LimitRow key={row.key} row={row} />
        ))}
      </dl>
    </>
  );
}

/** Only a mailbox that cannot send normally gets a pill, with Go's reason beside it when there is one. */
function MailboxStatus({ mailbox }: { mailbox: OutreachMailboxHealthItem }) {
  const pill = mailboxStatusPill(mailbox.status);
  if (!pill) return null;
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <span className={cn(PILL_BASE, pill.cls)}>{pill.label}</span>
      {mailbox.pausedReason}
    </p>
  );
}

function LimitRow({ row }: { row: MailboxLimitRow }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="flex items-center gap-1.5 text-foreground">
        <span aria-hidden className="size-1.5 rounded-full bg-(--crm-headroom)" />
        {row.label}
      </dt>
      <dd className="text-right tabular-nums">
        <span className="font-medium text-foreground">{row.value}</span>
        {row.limit && <span className="ml-1 text-xs text-muted-foreground">{row.limit}</span>}
      </dd>
    </div>
  );
}
